import type { P1FormData } from "../p1/p1Types";
import type { P2FormData } from "../p2/p2Types";
import type { P3FormData } from "../p3/p3Types";
import { computeRiskLevel } from "../p3/p3Types";
import type { P4FormData } from "../p4/p4Types";
import type { P5FormData } from "../p5/p5Types";
import type { P6FormData } from "../p6/p6Types";
import type { P7FormData } from "../p7/p7Types";

export const NHAI_SAMPLE_TITLE =
    "NHAI Toll Collection Performance Audit (Sample)";

export const NHAI_P0_INITIAL_COMMENTS = `Performance audit of toll collection operations on National Highways (NHAI).
SAI: CAG of India | Period: FY 2020–21 to FY 2024–25
Audit type: Combined 3Es (economy, efficiency, effectiveness)
Trigger: Public concern / media attention; prior CAG Report No. 7/2023
Primary concern: Revenue integrity and user value-for-money on NHAI-operated national highways.`;

export const NHAI_P0_BRIEFING_MD = `# INITIAL BRIEFING
**Audit: Performance Audit of Toll Collection Operations on National Highways (NHAI)**
**SAI: Comptroller and Auditor General of India**
**Period: FY 2020–21 to FY 2024–25**

## 1. Context and Audit Justification
India's national highway toll revenue reached over ₹64,800 crore in FY 2023–24. CAG Report No. 7/2023 identified ₹124 crore collected in violation of NH Fee Rules during construction delays.

## 2. Audit Object
NHAI toll collection operations: FASTag/RFID ETC, revenue integrity, oversight, and GNSS transition governance.

## 3. Preliminary Audit Questions
| 3E | Question |
|---|---|
| Economy | Are collection costs proportionate to revenue? |
| Efficiency | Is FASTag operating with minimal leakage? |
| Effectiveness | Are operations compliant with NH Fee Rules?`;

function risk(
    code: string,
    summary: string,
    impact: "low" | "medium" | "high",
    probability: "low" | "medium" | "high",
    cluster: "revenue_integrity" | "operational_efficiency" | "governance",
    dimension: string,
    audit_response: string,
): P3FormData["risks"][0] {
    return {
        code,
        summary,
        causes: [],
        event: summary,
        consequences: "",
        citizen_impact: "",
        impact,
        probability,
        level: computeRiskLevel(impact, probability),
        dimension,
        cluster,
        audit_response,
    };
}

export const NHAI_P1_FORM: P1FormData = {
    subject_matter:
        "NHAI's toll collection system on national highways — revenue collection, integrity controls, operational oversight, and GNSS transition management.",
    delimitation: {
        temporal: "FY 2020–21 to FY 2024–25",
        spatial: "NHAI-operated national highways (all regions)",
        institutional: "NHAI HQ, Regional Offices, IHMCL, selected toll plazas",
        functional: "Toll collection, revenue monitoring, oversight",
    },
    criteria: [
        { code: "C-01", source: "NH Fee Rules 2008", standards: "Fee levy, exemptions, collection timelines", type: "statutory" },
        { code: "C-02", source: "NHAI Act 1988", standards: "Statutory mandate for revenue collection", type: "statutory" },
        { code: "C-03", source: "MoRTH directives", standards: "FASTag mandatoriness, barrier-free tolling", type: "policy" },
        { code: "C-04", source: "NHAI SOPs", standards: "Plaza operations, reconciliation", type: "operational" },
        { code: "C-07", source: "International benchmarks", standards: "E-ZPass, TollCollect, UK ANPR systems", type: "benchmark" },
    ],
    evidence_strategy: [
        { type: "Documentary", sources: "NHAI Annual Reports, CAG 7/2023", technique: "Desk review" },
        { type: "Analytical", sources: "IHMCL FASTag datasets", technique: "Data analytics" },
        { type: "Testimonial", sources: "NHAI, IHMCL officials", technique: "Interviews" },
        { type: "Physical", sources: "Selected toll plazas", technique: "Site visits" },
    ],
    audit_problem:
        "Gap between toll revenue collected and service quality; revenue leakage, non-compliant fee levy, and rising volumes without verified assurance framework.",
    problem_bullets: [
        "Revenue leakage through FASTag evasion and exemption misuse",
        "Non-compliant fee collection (₹124 crore, CAG 2023)",
        "Rapidly rising volumes without independent revenue integrity assurance",
        "GNSS transition governance gap",
    ],
    stakeholders: [
        { name: "NHAI", interest: "Maximise revenue", influence: "high", interest_level: "high", relevance: "Primary auditee" },
        { name: "MoRTH", interest: "Policy compliance", influence: "high", interest_level: "high", relevance: "Criteria source" },
        { name: "IHMCL", interest: "System uptime", influence: "high", interest_level: "low", relevance: "ETC data source" },
        { name: "Road Users", interest: "Fair tolls", influence: "low", interest_level: "high", relevance: "Public interest" },
        { name: "Parliament/PAC", interest: "Accountability", influence: "high", interest_level: "high", relevance: "Report users" },
    ],
    assurance_level: "reasonable",
    assurance_justification: "Sufficient data from IHMCL, NHAI reports, and prior CAG findings support reasonable assurance per ISSAI 3000.",
    objective:
        "To assess, for FY 2020–21 to FY 2024–25, whether NHAI's toll collection operations have been conducted economically, efficiently, and effectively — examining collection costs, FASTag integrity, and oversight systems.",
    smart_checks: [
        { criterion: "Specific", met: true, basis: "Toll collection, 3Es, oversight" },
        { criterion: "Measurable", met: true, basis: "Leakage rates, cost ratios, compliance" },
        { criterion: "Achievable", met: true, basis: "IHMCL/NHAI public data" },
        { criterion: "Relevant", met: true, basis: "Public concern and prior CAG findings" },
        { criterion: "Time-bound", met: true, basis: "FY 2020–21 to FY 2024–25" },
    ],
    out_of_scope: [
        "State highway tolls",
        "Road construction quality",
        "BOT concessionaire internal finances",
        "GNSS system design/procurement",
    ],
    scope: [
        { dimension: "Entities", delimitation: "NHAI HQ; 6–8 ROs; IHMCL" },
        { dimension: "Plazas", delimitation: "~40–50 from 1,015 operational" },
        { dimension: "Period", delimitation: "FY 2020–21 to FY 2024–25" },
    ],
    engagement_risks: [
        { code: "ENG-01", risk: "IHMCL data unavailable", impact: "High", probability: "Medium", level: "High", response: "Early data request; alternative sources" },
        { code: "ENG-02", risk: "Scope creep", impact: "Medium", probability: "High", level: "High", response: "Strict scope discipline" },
        { code: "ENG-03", risk: "Methodology dispute", impact: "High", probability: "Medium", level: "High", response: "Pre-agree methodology" },
    ],
    deliverables_tracker: [
        { phase: "Planning", stage: "P0", deliverable: "Initial Briefing", status: "complete" },
        { phase: "Planning", stage: "P1", deliverable: "Audit Canvas", status: "complete" },
        { phase: "Planning", stage: "P2", deliverable: "Subject Matter Overview", status: "pending" },
    ],
    timeline: [
        { milestone: "P1 Canvas approved", target: "Week 1" },
        { milestone: "P6/P7 Plan approved", target: "Week 7–8" },
        { milestone: "Fieldwork", target: "Week 9" },
        { milestone: "Final Report", target: "Week 28–30" },
    ],
    benefits_financial: [
        { description: "Recovery of irregular toll revenue", value: "To be quantified" },
        { description: "Systemic leakage closure across 1,015 plazas", value: "To be quantified" },
        { description: "Collection cost ratio improvement", value: "To be quantified" },
    ],
    benefits_non_financial: [
        { description: "Parliamentary assurance on ₹40,000+ crore revenue stream", value: "High public interest" },
        { description: "Evidence for Highway Tariff Authority proposal", value: "High policy impact" },
        { description: "Public trust in fair toll collection", value: "High reputational value" },
    ],
    process_flow: [
        "Vehicle detection",
        "FASTag transaction",
        "Settlement",
        "Reconciliation",
        "Remittance",
        "Reporting",
        "Oversight",
    ],
};

export const NHAI_P2_FORM: P2FormData = {
    mandate: "NHAI is the nodal agency for national highways development, maintenance, and toll revenue collection under NHAI Act 1988 and NH Fee Rules 2008.",
    legal_instruments: [
        { norm: "National Highways Act 1988", provision: "Empowers toll levy on national highways" },
        { norm: "NH Fee Rules 2008", provision: "Fee structures, exemptions, collection conditions" },
        { norm: "CMVR 2021", provision: "FASTag mandatory from Feb 2021" },
    ],
    org_structure: `MoRTH → NHAI HQ → Revenue/Toll Division, IHMCL → 24+ Regional Offices → 1,015 Toll Plazas`,
    highlighted_entities: ["IHMCL", "Revenue / Toll Management Division (HQ)"],
    financial_years: [
        { year: "FY21", amount: "27926", growth: "+1.5%" },
        { year: "FY22", amount: "33928", growth: "+21.5%" },
        { year: "FY23", amount: "48032", growth: "+41.6%" },
        { year: "FY24", amount: "55882", growth: "+16.3%" },
        { year: "FY25", amount: "28823", growth: "—" },
    ],
    financial_significance: [
        "5-year cumulative ~₹1,94,593 crore",
        "1% leakage ≈ ₹1,946 crore/year",
        "FY24 settlement gap ~₹4,736 crore",
    ],
    process_steps: "Vehicle → FASTag reader → Class verification (ANPR) → Balance check → Deduction → IHMCL log → Reconciliation → NHAI remittance → Oversight",
    control_points: [
        { process: "RFID detection", risk: "Reader malfunction", control: "Maintenance SLA" },
        { process: "Class verification", risk: "Class mismatch fraud", control: "KYV/OVOT" },
        { process: "IHMCL reconciliation", risk: "Settlement gaps", control: "Daily matching" },
    ],
    systems: [
        { name: "FASTag/RFID ETC", info: "98% of transactions via FASTag" },
        { name: "NETC/NPCI", info: "Clearing and settlement platform" },
    ],
    tech_controls: [
        { control: "FASTag mandatory", year: "2021", purpose: "Eliminate cash evasion" },
        { control: "KYV", year: "2024", purpose: "Address class mismatch" },
        { control: "OVOT", year: "2024", purpose: "Single active tag enforcement" },
    ],
    cost_profile: [
        { period: "FY20–23", rate: "17.27%", context: "Pre-reform baseline" },
        { period: "FY24–25", rate: "9.27%", context: "Post-reform improvement" },
        { period: "International", rate: "3–7%", context: "ETC benchmark range" },
    ],
    milestones: [
        { event: "CAG Report 7/2023", year: "2023", significance: "Prior findings", flag: "follow-up" },
        { event: "Collection cost reform", year: "FY25", significance: "17.27% → 9.27%", flag: "mitigation" },
        { event: "GNSS pilot", year: "2025", significance: "Technology transition", flag: "risk" },
    ],
    critical_risks_preview: [
        { area: "Class mismatch fraud", dimension: "Efficiency", cluster: "Revenue Integrity" },
        { area: "Non-compliant fee levy", dimension: "Effectiveness", cluster: "Revenue Integrity" },
        { area: "Settlement gap", dimension: "Economy", cluster: "Operational Efficiency" },
    ],
    leakage_vectors: [
        { vector: "Class mismatch", description: "Commercial vehicles using personal FASTags", evidence: "KYV introduced 2024", severity: "confirmed" },
        { vector: "Irregular fee levy", description: "Toll during construction delays", evidence: "₹124 crore CAG 2023", severity: "confirmed" },
        { vector: "Settlement leakage", description: "Plaza vs NHAI remittance gap", evidence: "₹4,736 crore FY24", severity: "probable" },
    ],
    swot: {
        strengths: ["98% FASTag penetration", "Substantial revenue growth"],
        weaknesses: ["Decentralised plaza oversight", "High historical collection costs"],
        opportunities: ["UFCA contract reforms", "Real-time monitoring dashboards"],
        threats: ["GNSS transition control gaps", "Prior recommendation non-implementation"],
    },
};

export const NHAI_P3_FORM: P3FormData = {
    risks: [
        risk("RIS-01", "Vehicle Class Mismatch Fraud", "high", "high", "revenue_integrity", "Efficiency/Effectiveness", "Data analytics; ANPR sample"),
        risk("RIS-02", "Exemption Category Abuse", "medium", "high", "revenue_integrity", "Effectiveness", "Exempt tag register analysis"),
        risk("RIS-03", "Non-Compliant Fee Levy", "high", "high", "revenue_integrity", "Effectiveness/Compliance", "Levy authorisation vs completion dates"),
        risk("RIS-04", "Unauthorised Rate Revisions", "medium", "medium", "revenue_integrity", "Effectiveness", "Rate revision audit trail"),
        risk("RIS-05", "Settlement Leakage", "high", "medium", "operational_efficiency", "Economy/Efficiency", "End-to-end reconciliation"),
        risk("RIS-06", "Excessive Collection Costs", "high", "high", "operational_efficiency", "Economy", "UFCA contract review; benchmarking"),
        risk("RIS-07", "System Downtime & Failures", "medium", "high", "operational_efficiency", "Efficiency", "IHMCL dispute data review"),
        risk("RIS-08", "Inadequate Plaza Oversight", "high", "medium", "governance", "Effectiveness", "Oversight protocol review"),
        risk("RIS-09", "GNSS Transition Gap", "medium", "medium", "governance", "Effectiveness", "Contextual + forward recommendation"),
        risk("RIS-10", "Non-Implementation of Prior Findings", "high", "medium", "governance", "Effectiveness", "ATR systematic review"),
    ],
};

export const NHAI_P4_FORM: P4FormData = {
    problem_statement: "Are NHAI toll collection operations economic, efficient, and effective over FY 2020–25?",
    fundamental_question:
        "Whether NHAI's toll collection achieved economy, efficiency, and effectiveness across the audit period.",
    questions: [
        {
            code: "QST-01",
            title: "Economy of Toll Collection",
            text: "Were collection costs proportionate and minimised?",
            dimension: "economy",
            linked_risks: ["RIS-06"],
            sub_questions: ["Cost ratio trend", "UFCA contract management"],
            rows: [{
                required_info: "Annual collection cost by year",
                sources: "NHAI Annual Reports",
                criteria: "GFR 2017; international benchmarks",
                procedures: "Trend analysis; contract review (30–40 UFCAs)",
                possible_evidence: "Cost ledgers; UFCA contracts",
                possible_findings: "Avoidable expenditure from deficient contract management",
            }],
        },
        {
            code: "QST-02",
            title: "FASTag Revenue Integrity",
            text: "Did FASTag operate with minimal leakage?",
            dimension: "efficiency",
            linked_risks: ["RIS-01", "RIS-02", "RIS-05", "RIS-07"],
            sub_questions: ["Class mismatch", "Settlement gaps"],
            rows: [{
                required_info: "IHMCL transaction data by vehicle class",
                sources: "IHMCL",
                criteria: "NH Fee Rules; KYV policy",
                procedures: "Data analytics; sample plaza cross-check",
                possible_evidence: "Transaction logs; ANPR images",
                possible_findings: "Systematic under-collection from class mismatch",
            }],
        },
        {
            code: "QST-03",
            title: "Regulatory Compliance",
            text: "Were fees collected per NH Fee Rules?",
            dimension: "effectiveness",
            linked_risks: ["RIS-03", "RIS-04"],
            sub_questions: ["Construction delay levy", "Rate revisions"],
            rows: [{
                required_info: "Toll levy authorisation vs project completion",
                sources: "NHAI PIU records",
                criteria: "NH Fee Rules 2008/2013",
                procedures: "National sample review",
                possible_evidence: "Levy orders; completion certificates",
                possible_findings: "Unlawful collection during construction delays",
            }],
        },
        {
            code: "QST-04",
            title: "Oversight Effectiveness",
            text: "Did oversight detect and address irregularities?",
            dimension: "effectiveness",
            linked_risks: ["RIS-08", "RIS-10"],
            sub_questions: ["RO monitoring", "ATR follow-up"],
            rows: [{
                required_info: "Oversight protocols and inspection records",
                sources: "NHAI HQ and ROs",
                criteria: "NHAI SOPs; CAG prior recommendations",
                procedures: "Protocol review; ATR assessment",
                possible_evidence: "Inspection logs; ATR submissions",
                possible_findings: "Ineffective oversight enabling recurring violations",
            }],
        },
    ],
    glossary: [{ term: "UFCA", definition: "User Fee Collection Agency" }],
    consistency_checks: [
        { id: "d01", text: "All questions specific and linked to objective", checked: false },
        { id: "d02", text: "All required columns present", checked: false },
        { id: "d09", text: "All High/Intolerable risks addressed", checked: false },
    ],
};

export const NHAI_P5_FORM: P5FormData = {
    instruments: [
        {
            code: "IG-01",
            name: "Interview Guide — NHAI HQ",
            type: "interview",
            target: "NHAI Finance/Toll Division",
            linked_qst: ["QST-01", "QST-04"],
            linked_procedures: ["PR-01e", "PR-04f"],
            duration: "90 min",
            opening_script: "CAG performance audit of NHAI toll collection FY20–25…",
            sections: [{
                name: "Collection costs & UFCA contracts",
                linked_qst: ["QST-01"],
                questions: ["How are UFCA fees determined?", "What drove the 17.27% → 9.27% reduction?"],
                follow_ups: [],
                notes: "",
            }],
            checklist_items: [],
            document_items: [],
            content_md: "",
        },
        {
            code: "DR-01",
            name: "Data Request — NHAI & IHMCL",
            type: "data_request",
            target: "NHAI HQ + IHMCL",
            linked_qst: ["QST-01", "QST-02", "QST-03", "QST-04"],
            linked_procedures: [],
            duration: "15 working days",
            opening_script: "",
            sections: [],
            checklist_items: [],
            document_items: [
                { item: "IHMCL monthly transactions by vehicle class", document: "FY21–FY25", period: "5 years" },
                { item: "UFCA contract files", document: "30–40 contracts", period: "FY20–25" },
            ],
            content_md: "",
        },
    ],
    sampling: {
        method: "Stratified — high/medium revenue, prior findings, geographic spread",
        sample_size: "40–50 plazas from 1,015; 10–15 site visits",
        limitations: "IHMCL vehicle-class data access pending authorisation",
    },
};

export const NHAI_P6_FORM: P6FormData = {
    legal_basis: "CAG constitutional mandate; NHAI Act 1988 subject to CAG audit.",
    team: [
        { name: "Lead Auditor", role: "Audit Director", allocation: "30%" },
        { name: "Senior Auditor", role: "Fieldwork lead", allocation: "80%" },
    ],
    milestones: [
        { code: "M-01", activity: "P7 gate approval", responsible: "Audit Director", start_date: "W8", end_date: "W8", status: "planned", phase: "planning" },
        { code: "M-02", activity: "Fieldwork E0–E3", responsible: "Audit team", start_date: "W9", end_date: "W20", status: "planned", phase: "execution" },
        { code: "M-03", activity: "Draft report E5", responsible: "Lead Auditor", start_date: "W21", end_date: "W24", status: "planned", phase: "reporting" },
        { code: "M-04", activity: "Adversarial process", responsible: "NHAI response", start_date: "W25", end_date: "W27", status: "planned", phase: "reporting" },
    ],
    resources: {
        auditor_hours: "1,200",
        supervisor_hours: "200",
        travel_notes: "Multi-region plaza visits; NHAI HQ and IHMCL Delhi",
    },
    planning_risks: [
        { risk: "IHMCL data access", probability: "Medium", mitigation: "Director-level authorisation letter" },
        { risk: "Scope creep", probability: "High", mitigation: "Document out-of-scope items" },
    ],
    executive_summary: `CAG performance audit assessing NHAI toll collection (FY20–25) across 3Es. Four audit questions address 10 risks. Scope: NHAI HQ, 6–8 ROs, IHMCL, ~40–50 plazas. Mixed methods: analytics, document review, interviews, site visits. Timeline: 28–30 weeks.`,
    document_status: "draft",
};

export const NHAI_P7_FORM: P7FormData = {
    gate_status: "pending",
    checklist: [
        { id: "A01", category: "A. Scope & Objectives", criterion: "Audit objective is SMART and aligned with mandate", status: "pass", notes: "", weight: 2 },
        { id: "A04", category: "A. Scope", criterion: "Scope clearly defined: entities, plazas, period", status: "pass", notes: "", weight: 2 },
        { id: "A06", category: "A. Feasibility", criterion: "Audit is auditeable — data accessible", status: "flag", notes: "IHMCL vehicle-class data access pending", weight: 3, owner: "Lead Auditor" },
        { id: "D04", category: "D. Procedures", criterion: "Procedures specific, actionable, feasible", status: "pass", notes: "", weight: 2 },
        { id: "E06", category: "E. Sampling", criterion: "Sample sizes proportionate to objectives", status: "pass", notes: "", weight: 1 },
    ],
    blockers: "IHMCL data access authorisation required before E0.",
    slides: [
        { number: 1, title: "Audit Title", key_points: "NHAI toll collection | CAG | FY20–25", visual: "Title card with highway map" },
        { number: 2, title: "Audit Object", key_points: "1,015 plazas; FASTag ecosystem; 5-year period", visual: "Process flow diagram" },
        { number: 3, title: "Materiality", key_points: "₹1,94,593 crore cumulative; 1% leakage = ₹1,946 crore/yr", visual: "Revenue bar chart" },
        { number: 4, title: "Risk Matrix", key_points: "3 intolerable; 5 high; 2 moderate", visual: "Risk heatmap" },
        { number: 5, title: "Audit Questions", key_points: "QST-01 to QST-04 covering all risks", visual: "4-card 3E strip" },
    ],
    override_justification: "",
};

export const NHAI_DELIVERABLES: Record<string, string> = {
    p0: NHAI_P0_BRIEFING_MD,
    p1: "# AUDIT CANVAS\n\n**NHAI Toll Collection | CAG of India | Combined 3Es**\n\nSee form inputs for full 13-component canvas.",
    p2: "# SUBJECT MATTER OVERVIEW\n\n**Toll Collection — NHAI | FY 2020–21 to FY 2024–25**",
    p3: "# RISK MATRIX\n\n**10 risks across Revenue Integrity, Operational Efficiency, and Governance clusters.**",
    p4: "# AUDIT DESIGN MATRIX\n\n**4 audit questions (QST-01 to QST-04) mapped to risks and procedures.**",
    p5: "# DATA COLLECTION INSTRUMENTS\n\n**IG-01, IG-02, IG-03, DR-01, observation checklists.**",
    p6: "# CONSOLIDATED AUDIT PLAN\n\n**Executive summary, timeline, team, and resource allocation.**",
    p7: "# QUALITY REVIEW\n\n**Mandatory human gate — 70% adherence required to advance to E0.**",
};
